import fetch from 'isomorphic-fetch';
import i18n from '../../../../i18n';
import {
  LOGIN_START,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  CHECK_LOGIN_START,
  CHECK_LOGIN_SUCCESS,
  CHECK_LOGIN_FAILURE,
  SESSION_PROBE_SKIPPED,
  GET_KEYCLOAK_URL_START,
  GET_KEYCLOAK_URL_SUCCESS,
  GET_KEYCLOAK_URL_FAILURE,
  USER_DETAIL,
  GET_USER_DETAIL_START,
  GET_USER_DETAIL_FAILURE,
  GET_USER_DETAIL_SUCCESS,
} from './events';
import {
  API_LOGIN,
  NOTIFICATION_TYPES,
  HTTP_NOT_FOUND_RESPONSE,
  API_KEYCLOAK_URL,
  GET_USER_DETAIL_API,
  AUTH_PUBLIC_PATHS,
} from '../../constants';
import { addFeatureTogglePermissions, addSchedulingPermissions, storeUserDetail, getNodes } from '../../actions/userActions';
import { isJwtTokenValid } from '../../utils/authToken';
import {
  getGetKeycloakUrlInFlight,
  setGetKeycloakUrlInFlight,
  getUserInfoSyncInFlight,
  setUserInfoSyncInFlight,
  markUserInfoSyncSuccess,
  normalizeKeycloakConfig,
  shouldSkipUserInfoSync,
} from '../../utils/authFlow';

export const userDetail = response => ({
  type: USER_DETAIL,
  response,
});

export const startGetKeyCloakURL = () => ({
  type: GET_KEYCLOAK_URL_START,
});

export const getKeyCloakURLSuccess = response => ({
  type: GET_KEYCLOAK_URL_SUCCESS,
  response,
});

export const getKeyCloakURLFailure = error => ({
  type: GET_KEYCLOAK_URL_FAILURE,
  error,
});

export const getKeyCloakURL = async () =>
  async (dispatch, getState) => {
    const existingConfig = normalizeKeycloakConfig(getState().LoginReducer.get('keyCloakConfig'));
    if (existingConfig) {
      return existingConfig;
    }
    if (getGetKeycloakUrlInFlight()) {
      return getGetKeycloakUrlInFlight();
    }

    const request = (async () => {
      try {
        dispatch(startGetKeyCloakURL());
        const url = API_KEYCLOAK_URL;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }).then((res) => {
          if (res.status >= 400) {
            throw new Error(i18n.t(NOTIFICATION_TYPES.ERROR_MESSAGE_FROM_SERVER));
          }
          return res.json();
        });
        dispatch(getKeyCloakURLSuccess(response));
        window.localStorage.setItem('keycloakUrl', `${response.url}/realms/${response.realm}/account/password`);
        return response;
      } catch (e) {
        dispatch(getKeyCloakURLFailure(e));
        return e;
      } finally {
        setGetKeycloakUrlInFlight(null);
      }
    })();

    setGetKeycloakUrlInFlight(request);
    return request;
  };

export const startLogin = () => ({
  type: LOGIN_START,
});

export const loginSuccess = response => ({
  type: LOGIN_SUCCESS,
  response,
});

export const loginFailure = error => ({
  type: LOGIN_FAILURE,
  error,
});

export const login = async (email, password) =>
  async (dispatch) => {
    try {
      dispatch(startLogin());
      const url = API_LOGIN;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
        }),
      }).then((res) => {
        // Check API Not Found
        if (res.status === HTTP_NOT_FOUND_RESPONSE.CODE) {
          throw new Error(HTTP_NOT_FOUND_RESPONSE.MESSAGE);
        }
        return res.json();
      });
      // Success
      dispatch(loginSuccess(response));
    } catch (e) {
      // Dispatch to failure
      dispatch(loginFailure(e));
    }
  };

export const getStartUserInfo = () => ({
  type: GET_USER_DETAIL_START,
});

export const getUserInfoSuccess = response => ({
  type: GET_USER_DETAIL_SUCCESS,
  response,
});

export const getUserInfoFailure = error => ({
  type: GET_USER_DETAIL_FAILURE,
  error,
});

export const checkLoginStart = () => ({
  type: CHECK_LOGIN_START,
});

export const checkLoginFailure = error => ({
  type: CHECK_LOGIN_FAILURE,
  error,
});

export const skipSessionProbe = () => ({
  type: SESSION_PROBE_SKIPPED,
});

export const shouldSkipSessionProbe = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return AUTH_PUBLIC_PATHS.includes(window.location.pathname);
};

export const checkLoginSuccess = (response) => {
  const userResponse = addFeatureTogglePermissions(response);
  const res = addSchedulingPermissions(userResponse);
  return {
    type: CHECK_LOGIN_SUCCESS,
    userDetail: res,
  };
};

let checkLoginInFlight = null;

export const checkLogin = (options = {}) => async (dispatch) => {
  const { restoreSession = false } = options;
  if (checkLoginInFlight) {
    return checkLoginInFlight;
  }

  checkLoginInFlight = (async () => {
    try {
      dispatch(checkLoginStart());
      const url = API_LOGIN;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
        credentials: 'include',
      }).then((res) => {
        if (res.status >= 400) {
          throw new Error(i18n.t(NOTIFICATION_TYPES.ERROR_MESSAGE_FROM_SERVER));
        }
        return res.json();
      });

      const loginAction = checkLoginSuccess(response);
      dispatch(loginAction);

      if (restoreSession && !response.code) {
        const jwtToken = isJwtTokenValid(response.jwtToken)
          ? response.jwtToken
          : options.fallbackJwtToken;
        if (isJwtTokenValid(jwtToken)) {
          const hydratedUser = { ...loginAction.userDetail, jwtToken };
          dispatch(storeUserDetail(hydratedUser));
          await dispatch(getNodes({
            ...hydratedUser,
            loginTime: Date.now(),
          }));
        }
      }

      return response;
    } catch (e) {
      dispatch(checkLoginFailure(e));
      return e;
    } finally {
      checkLoginInFlight = null;
    }
  })();

  return checkLoginInFlight;
};

export const getUserInfoDetails = async (token, idToken, refreshToken, program) =>
  async (dispatch, getState) => {
    if (shouldSkipUserInfoSync(token)) {
      return getUserInfoSyncInFlight() || Promise.resolve();
    }
    if (getUserInfoSyncInFlight()) {
      return getUserInfoSyncInFlight();
    }

    const request = (async () => {
      try {
        dispatch(getStartUserInfo());
        const url = GET_USER_DETAIL_API;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            access_token: token,
            id_token: idToken,
            refresh_token: refreshToken,
            programCode: program,
          }),
        }).then((res) => {
          if (res.status === HTTP_NOT_FOUND_RESPONSE.CODE) {
            throw new Error(HTTP_NOT_FOUND_RESPONSE.MESSAGE);
          }
          return res.text();
        });
        dispatch(getUserInfoSuccess(response));
        const loginResponse = await dispatch(checkLogin({
          restoreSession: true,
          fallbackJwtToken: token,
        }));
        if (loginResponse && !loginResponse.code && getState().UserDetail.get('isLoggedIn')) {
          markUserInfoSyncSuccess(token);
        }
        return loginResponse;
      } catch (e) {
        dispatch(getUserInfoFailure(e));
        return e;
      } finally {
        setUserInfoSyncInFlight(null);
      }
    })();

    setUserInfoSyncInFlight(request);
    return request;
  };
